import { useEffect, useMemo, useState } from "react";
import { Search, ShoppingBag, MapPin, Store } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { MenuItem } from "@/components/menu/MenuItem";
import { OrderSheet } from "@/components/menu/OrderSheet";
import { api, formatRupiah } from "@/lib/api";
import { EMPTY_SEL, hasOptions, makeLine } from "@/lib/options";
import { OptionPicker } from "@/components/OptionPicker";

export default function Menu() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("Semua");
  const [lines, setLines] = useState([]);
  const [picking, setPicking] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/public/menu").then((r) => setData(r.data)).catch(() => setError(true));
  }, []);

  const products = data?.products || [];
  const categories = useMemo(() => ["Semua", ...new Set(products.map((p) => p.category))], [products]);
  const filtered = products.filter((p) => (cat === "Semua" || p.category === cat) && p.name.toLowerCase().includes(search.toLowerCase()));
  const cart = lines;
  const total = cart.reduce((s, c) => s + c.price * c.qty, 0);
  const count = cart.reduce((s, c) => s + c.qty, 0);
  const qtyOf = (id) => lines.filter((l) => l.id === id).reduce((s, l) => s + l.qty, 0);

  const addLine = (p, sel, n) => {
    const line = makeLine(p, sel, n);
    setLines((ls) => (ls.some((x) => x.key === line.key) ? ls.map((x) => (x.key === line.key ? { ...x, qty: Math.min(99, x.qty + n) } : x)) : [...ls, line]));
  };
  const change = (p, d) => {
    if (d > 0) return hasOptions(p) ? setPicking(p) : addLine(p, EMPTY_SEL, 1);
    setLines((ls) => {
      const i = ls.map((l) => l.id).lastIndexOf(p.id);
      return i < 0 ? ls : ls.map((l, j) => (j === i ? { ...l, qty: l.qty - 1 } : l)).filter((l) => l.qty > 0);
    });
  };

  if (error) return <p className="p-8 text-center text-slate-500" data-testid="menu-error">Menu tidak dapat dimuat. Coba lagi nanti.</p>;
  if (!data) return <p className="p-8 text-center text-slate-500" data-testid="menu-loading">Memuat menu...</p>;

  return (
    <div className="min-h-screen bg-slate-50 pb-28" data-testid="menu-page">
      <header className="grain relative overflow-hidden bg-slate-900 px-5 pb-8 pt-10 text-white">
        <div className="relative mx-auto max-w-xl">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-500"><Store className="h-5 w-5" /></div>
          <h1 className="mt-4 font-display text-3xl font-extrabold tracking-tight sm:text-4xl" data-testid="menu-store-name">{data.store.name}</h1>
          {data.store.address && (
            <p className="mt-2 flex items-start gap-1.5 text-sm text-slate-300" data-testid="menu-store-address"><MapPin className="mt-0.5 h-4 w-4 shrink-0" />{data.store.address}</p>
          )}
          <p className="mt-3 text-sm text-emerald-300">Pilih menu, lalu kirim pesanan lewat WhatsApp.</p>
        </div>
      </header>

      <main className="mx-auto max-w-xl space-y-4 px-4 pt-5">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari menu..." className="h-11 bg-white pl-9" data-testid="menu-search-input" />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {categories.map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`menu-category-${c.toLowerCase()}`}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${cat === c ? "bg-slate-900 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200"}`}>
              {c}
            </button>
          ))}
        </div>
        <div className="space-y-3">
          {filtered.map((p) => <MenuItem key={p.id} p={p} qty={qtyOf(p.id)} onChange={change} />)}
          {filtered.length === 0 && <p className="py-10 text-center text-sm text-slate-500" data-testid="menu-empty">Menu tidak ditemukan.</p>}
        </div>
      </main>

      {count > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white/95 p-3 backdrop-blur">
          <Button onClick={() => setOpen(true)} className="mx-auto flex h-12 w-full max-w-xl justify-between bg-emerald-600 hover:bg-emerald-700" data-testid="menu-checkout-button">
            <span className="flex items-center gap-2"><ShoppingBag className="h-4 w-4" /> <span data-testid="menu-cart-count">{count}</span> item</span>
            <span className="font-mono font-bold" data-testid="menu-cart-total">{formatRupiah(total)}</span>
          </Button>
        </div>
      )}

      <OptionPicker product={picking} onClose={() => setPicking(null)} onAdd={(sel, n) => { addLine(picking, sel, n); setPicking(null); }} />
      <OrderSheet open={open} onOpenChange={setOpen} store={data.store} cart={cart} total={total} />
    </div>
  );
}
